/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { FieldValues, UseFormProps, useForm } from 'react-hook-form'
import { AnyObjectSchema } from 'yup'

import { ApiResult } from '@/lib/api/api-result'

/** The base state for a form. */
type BaseFormState = {
  /** Whether a request is pending. */
  requestPending: boolean

  /** Whether a request was successful. */
  requestSuccess: boolean

  /** The error message from a request. */
  requestError: string
}

/** The base form hook result. */
type BaseFormHookResult<T extends FieldValues, R> = ReturnType<typeof useForm<T>> &
  BaseFormState & {
    setRequestPending: (value: boolean) => void
    setRequestSuccess: (value: boolean) => void
    setRequestError: (value: string) => void
    onSubmit: (data: T) => Promise<ApiResult<R>>
  }

/**
 * A base form hook that handles form submission and request state.
 *
 * @param schema - the Yup schema to use for validation
 * @param submitFn - the function to call when the form is submitted
 * @param formOptions - the options to pass to the form
 */
const useBaseForm = <T extends FieldValues, R>(
  schema: AnyObjectSchema,
  submitFn: (data: T) => Promise<ApiResult<R>>,
  formOptions?: UseFormProps<T>,
): BaseFormHookResult<T, R> => {
  const [requestPending, setRequestPending] = useState(false)
  const [requestSuccess, setRequestSuccess] = useState(false)
  const [requestError, setRequestError] = useState('')

  const formMethods = useForm<T>({
    ...formOptions,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  })

  const onSubmit = async (data: T): Promise<ApiResult<R>> => {
    setRequestPending(true)
    setRequestSuccess(false)
    setRequestError('')

    try {
      const response = await submitFn(data)
      setRequestSuccess(response.success)

      if (!response.success) {
        setRequestError(response.error.message || 'An unexpected error occurred. Please try again.')
      }

      return response
    } catch (error) {
      console.error(error)
      setRequestSuccess(false)
      setRequestError('An unexpected error occurred. Please try again.')
      return { success: false, error: { code: 500, message: 'An unexpected error occurred. Please try again.' } }
    } finally {
      setRequestPending(false)
    }
  }

  return {
    ...formMethods,
    requestPending,
    setRequestPending,
    requestSuccess,
    setRequestSuccess,
    requestError,
    setRequestError,
    onSubmit,
  }
}

export { useBaseForm }
